import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { cn } from "@/lib/utils";
import { COMPANY_STATUS_CONFIG, COMPANY_BILLING_MODEL_LABEL } from "@/lib/status-configs";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import type { CompanyBillingModel } from "@/types";
import { Buildings } from "@phosphor-icons/react";
import type { Company } from "@/types";

const STATUS_CONFIG = COMPANY_STATUS_CONFIG;

// Create accepts a couple of fields that don't live on Company directly:
// initial prepaid credit (KM, recorded via the ledger), the starting rabat and
// the billing model. The rest of the billing terms live in "Uslovi naplate"
// on the company detail page.
export type CompanyFormSubmit = Partial<Company> & {
  initialCredit?: number;
  discountPercent?: number;
};

export interface CompanyFormDialogProps {
  company: Company | null;
  isPending: boolean;
  onSubmit: (data: CompanyFormSubmit) => void;
  onClose: () => void;
}

export function CompanyFormDialog({
  company,
  isPending,
  onSubmit,
  onClose,
}: CompanyFormDialogProps) {
  const [name, setName] = useState(company?.name ?? "");
  const [planType, setPlanType] = useState(company?.planType ?? "");
  const [maxEmployees, setMaxEmployees] = useState(
    String(company?.maxEmployees ?? "50")
  );
  const [billingEmail, setBillingEmail] = useState(
    company?.billingEmail ?? ""
  );
  const [notes, setNotes] = useState(company?.notes ?? "");
  const [status, setStatus] = useState<"active" | "suspended">(
    company?.status ?? "active"
  );
  const [anonymizeEmployees, setAnonymizeEmployees] = useState<boolean>(
    !!company?.anonymizeEmployees
  );
  // Create-only: starting credit (KM) and discount (%). On edit these are
  // managed in the credit section of the company detail page.
  const [initialCredit, setInitialCredit] = useState<number | null>(null);
  const [discountPercent, setDiscountPercent] = useState("0");
  const [billingModel, setBillingModel] = useState<CompanyBillingModel>("credit");

  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: CompanyFormSubmit = {
      name: name.trim(),
      planType: planType.trim(),
      maxEmployees: Number(maxEmployees),
      billingEmail: billingEmail.trim(),
      notes: notes.trim() ? notes.trim() : null,
      anonymizeEmployees,
    };
    if (company) {
      data.status = status;
    } else {
      data.billingModel = billingModel;
      data.initialCredit = billingModel === "credit" ? initialCredit ?? 0 : 0;
      data.discountPercent = Number(discountPercent) || 0;
    }
    onSubmit(data);
  };

  return (
    <Dialog open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={Buildings}
            title={company ? "Uredi firmu" : "Nova firma"}
            description={
              company
                ? "Izmijenite detalje postojeće firme."
                : "Registrujte novu firmu na platformi."
            }
          />
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label htmlFor="co-name">Naziv firme</Label>
              <Input
                id="co-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="npr. TechFirma d.o.o."
                maxLength={200}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="co-plan">Tip plana</Label>
                <Input
                  id="co-plan"
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value)}
                  placeholder="npr. premium"
                  maxLength={100}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="co-email">Billing email</Label>
                <Input
                  id="co-email"
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder="billing@firma.ba"
                  maxLength={200}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="co-max">Maks. zaposlenih</Label>
              <Input
                id="co-max"
                type="number"
                min="1"
                max={10000}
                step="1"
                value={maxEmployees}
                onChange={(e) => setMaxEmployees(e.target.value)}
                required
              />
            </div>

            {/* Credit fields — only when creating; on edit, credit lives in the
                credit section of the company detail page. */}
            {!company && (
              <div className="space-y-2">
                <Label>Model naplate</Label>
                <SegmentedFilter
                  value={billingModel}
                  onChange={setBillingModel}
                  options={(["credit", "invoice"] as const).map((v) => ({ value: v, label: COMPANY_BILLING_MODEL_LABEL[v] }))}
                />
              </div>
            )}

            {!company && (
              <div className="grid grid-cols-2 gap-4">
                {billingModel === "credit" ? (
                <div className="space-y-2">
                  <Label htmlFor="co-credit">Početni kredit</Label>
                  <CurrencyInput
                    id="co-credit"
                    value={initialCredit}
                    onChange={setInitialCredit}
                    placeholder="npr. 500,00"
                  />
                  <p className="text-xs text-muted-foreground">
                    Bilježi se kao dopuna u historiji kredita.
                  </p>
                </div>
                ) : (
                  <p className="self-end text-xs text-muted-foreground">
                    Firma dobija jednu fakturu na kraju mjeseca.
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="co-discount">Rabat (%)</Label>
                  <Input
                    id="co-discount"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Ide samo iz provizije platforme.</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="co-notes">Ugovor / napomene</Label>
              <textarea
                id="co-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="npr. premium plan, 4 sesije/zaposleni/mj, broj ugovora…"
                className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <p className="text-xs text-muted-foreground">
                Informativno, ne utiče na naplatu. Uslovi naplate se podešavaju na stranici firme.
              </p>
            </div>

            {company && (
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex gap-2">
                  {(["active", "suspended"] as const).map((s) => {
                    const cfg = STATUS_CONFIG[s];
                    const selected = status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={cn(
                          "flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all",
                          selected
                            ? cn(cfg.bg, cfg.color, "ring-1", cfg.ring)
                            : "border-border text-muted-foreground hover:border-border/80"
                        )}
                      >
                        <span
                          className={cn(
                            "size-2 rounded-full",
                            selected ? cfg.dot : "bg-muted-foreground/30"
                          )}
                        />
                        {cfg.label.slice(0, -1)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox
                id="anonymizeEmployees"
                checked={anonymizeEmployees}
                onCheckedChange={(v) => setAnonymizeEmployees(v === true)}
                className="mt-1"
              />
              <div>
                <Label htmlFor="anonymizeEmployees" className="font-medium">
                  {t("admin.companies.anonymizeEmployees.label")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("admin.companies.anonymizeEmployees.hint")}
                </p>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending
              ? "Spremanje..."
              : company
                ? "Spremi promjene"
                : "Kreiraj firmu"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
